import { __ } from '@wordpress/i18n';
import { setupLocationVisibility } from './cityFilter';
import { initMembershipSection } from './membershipRepeater';

const GROUPS = [
	{
		label: __( 'Personal data', 'anacrm' ),
		description: __(
			'This fields are part of your profile. They are mandatory to register in this activity.',
			'anacrm'
		),
		open: true,
		fields: [
			'_pe_firstname',
			'_pe_lastname',
			'_pe_gender',
			'_pe_contact_mail',
			'_pe_contact_phone',
			'_pe_dni',
			'_pe_year_birth',
			'_pe_territory',
			'_pe_city',
			'_pe_work_condition',
			'_pe_interest',
		],
	},
];

function findFieldWrapper( form, name ) {
	// Pods adds class pods-form-ui-row-name-{name} to every field container.
	// Underscores in the field name are converted to dashes in the class,
	// so _pe_gender → pods-form-ui-row-name--pe-gender (double dash from leading _).
	const dashedName = name.replace( /_/g, '-' );
	return form.querySelector( `.pods-form-ui-row-name-${ dashedName }` );
}

function setupFullName( form ) {
	// Attach to the form immediately — the inputs don't need to exist yet.
	// Every handler invocation queries the DOM fresh, so stale references
	// are never an issue even if React re-renders the nodes later.
	form.addEventListener( 'input', ( e ) => {
		const name = e.target.getAttribute( 'name' ) || '';
		if (
			! name.startsWith( 'pods_field__pe_firstname' ) &&
			! name.startsWith( 'pods_field__pe_lastname' )
		) {
			return;
		}
		const titleInput = form.querySelector(
			'input[name^="pods_field_post_title"]'
		);
		const firstInput = form.querySelector(
			'input[name^="pods_field__pe_firstname"]'
		);
		const lastInput = form.querySelector(
			'input[name^="pods_field__pe_lastname"]'
		);
		if ( ! titleInput || ! firstInput || ! lastInput ) {
			return;
		}
		titleInput.value = [ firstInput.value.trim(), lastInput.value.trim() ]
			.filter( Boolean )
			.join( ' ' );
	} );
}

function buildAccordion( form ) {
	for ( const group of GROUPS ) {
		const fieldWrappers = group.fields
			.map( ( f ) => findFieldWrapper( form, f ) )
			.filter( Boolean );
		if ( ! fieldWrappers.length ) {
			continue;
		}

		const parent = fieldWrappers[ 0 ].parentElement;

		const details = document.createElement( 'details' );
		if ( group.open ) {
			details.open = true;
		}
		details.className = 'mb-4 border-2 border-green';

		const summary = document.createElement( 'summary' );
		summary.className =
			'px-4 py-3 cursor-pointer select-none bg-green/30 hover:bg-green/60 marker:float-left';
		const summaryTit = document.createElement( 'h2' );
		summaryTit.className = 'text-base font-semibold inline-block';
		summaryTit.textContent = group.label;
		summary.appendChild( summaryTit );
		const summaryDesc = document.createElement( 'p' );
		summaryDesc.textContent = group.description ?? '';
		summary.appendChild( summaryDesc );
		details.appendChild( summary );

		const body = document.createElement( 'div' );
		body.className = 'px-4 py-2 bg-green/10';
		details.appendChild( body );

		parent.insertBefore( details, fieldWrappers[ 0 ] );
		fieldWrappers.forEach( ( w ) => body.appendChild( w ) );
	}
}

function buildOrgBlock( wrapper, submittableFields, submitRow, form ) {
	const personId = wrapper.dataset.personId ?? '';
	const isNew = wrapper.dataset.isEdit === '0';

	const details = document.createElement( 'details' );
	details.className = 'mb-4 border-2 border-green';

	const summary = document.createElement( 'summary' );
	summary.className =
		'px-4 py-3 cursor-pointer select-none bg-green/30 hover:bg-green/60 marker:float-left';
	const summaryTit = document.createElement( 'h2' );
	summaryTit.className = 'text-base font-semibold inline-block';
	summaryTit.textContent = __( 'Your organisation', 'anacrm' );
	summary.appendChild( summaryTit );
	const summaryDesc = document.createElement( 'p' );
	summaryDesc.textContent = __(
		'If you belong to an organisation or to an administration, please, tell us about it.',
		'anacrm'
	);
	summary.appendChild( summaryDesc );
	details.appendChild( summary );

	const body = document.createElement( 'div' );
	body.className = 'px-4 py-2 bg-green/10';
	details.appendChild( body );

	if ( submitRow ) {
		submittableFields.insertBefore( details, submitRow );
	} else if ( submittableFields ) {
		submittableFields.appendChild( details );
	} else {
		form.appendChild( details );
	}

	const orgContainer = document.createElement( 'div' );
	orgContainer.className = 'mb-4';
	const orgHeading = document.createElement( 'p' );
	orgHeading.className =
		'text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2';
	orgHeading.textContent = __( 'Organisations', 'anacrm' );
	orgContainer.appendChild( orgHeading );
	body.appendChild( orgContainer );

	const adContainer = document.createElement( 'div' );
	const adHeading = document.createElement( 'p' );
	adHeading.className =
		'text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2';
	adHeading.textContent = __( 'Administrations', 'anacrm' );
	adContainer.appendChild( adHeading );
	body.appendChild( adContainer );

	initMembershipSection( orgContainer, personId, isNew, { type: 'org' } );
	initMembershipSection( adContainer, personId, isNew, {
		type: 'administration',
	} );
}

function buildRegistrationBlock( wrapper, submittableFields, submitRow, form ) {
	const activityId = wrapper.dataset.activityId ?? '';
	let regFields = [];
	try {
		regFields = JSON.parse( wrapper.dataset.registrationFields || '[]' );
	} catch ( e ) {
		regFields = [];
	}

	const details = document.createElement( 'details' );
	details.open = true;
	details.className = 'mb-4 border-2 border-green';

	const summary = document.createElement( 'summary' );
	summary.className =
		'px-4 py-3 cursor-pointer select-none bg-green/30 hover:bg-green/60 marker:float-left';
	const summaryTit = document.createElement( 'h2' );
	summaryTit.className = 'text-base font-semibold inline-block';
	summaryTit.textContent = __( 'Registration information', 'anacrm' );
	summary.appendChild( summaryTit );
	const summaryDesc = document.createElement( 'p' );
	summaryDesc.textContent = __(
		'Tell us your registration preferences.',
		'anacrm'
	);
	summary.appendChild( summaryDesc );
	details.appendChild( summary );

	const body = document.createElement( 'div' );
	body.className = 'px-4 py-2 bg-green/10';
	details.appendChild( body );

	const activityIdInput = document.createElement( 'input' );
	activityIdInput.type = 'hidden';
	activityIdInput.name = 'anacrm_signup_activity_id';
	activityIdInput.value = activityId;
	body.appendChild( activityIdInput );

	regFields.forEach( ( f ) => {
		const row = document.createElement( 'div' );
		row.className = 'mb-4';
		const label = document.createElement( 'label' );
		label.className = 'block text-sm font-semibold mb-1';
		label.textContent = f.label;
		const input = document.createElement( 'input' );
		input.type = 'text';
		input.maxLength = 255;
		input.className =
			'block w-full border-2 border-green-dark px-3 py-2 text-sm bg-white mt-1';
		input.name = `anacrm_registration_field[${ f.key }]`;
		label.appendChild( input );
		row.appendChild( label );
		body.appendChild( row );
	} );

	if ( submitRow ) {
		submittableFields.insertBefore( details, submitRow );
	} else if ( submittableFields ) {
		submittableFields.appendChild( details );
	} else {
		form.appendChild( details );
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	const wrapper = document.querySelector(
		'.anacrm-activity-signup-form-accordion'
	);
	if ( ! wrapper ) {
		return;
	}

	const successDiv = wrapper.querySelector( '.pods-form-front-success' );
	if ( successDiv ) {
		const btn = document.createElement( 'button' );
		btn.type = 'button';
		btn.className = 'px-3 py-2 text-2xl hover:text-yellow cursor-pointer';
		btn.innerHTML = '&times;';
		btn.addEventListener( 'click', () => successDiv.remove() );
		successDiv.appendChild( btn );
	}

	const form = wrapper.querySelector( 'form' );
	if ( ! form ) {
		return;
	}

	// Full-name sync: attach immediately; the handler queries inputs on each keystroke.
	setupFullName( form );

	let done = false;

	function tryBuild() {
		if ( done ) {
			return;
		}
		if ( ! form.querySelector( '.pods-field__container' ) ) {
			return;
		}
		done = true;
		observer.disconnect();

		buildAccordion( form );
		setupLocationVisibility( form, {
			territoryField: '_pe_territory',
			cityField: '_pe_city',
		} );

		const submittableFields = form.querySelector(
			'.pods-submittable-fields'
		);
		const submitRow = submittableFields
			? submittableFields.querySelector( '.pods-submit' )
			: null;

		buildOrgBlock( wrapper, submittableFields, submitRow, form );
		buildRegistrationBlock( wrapper, submittableFields, submitRow, form );
	}

	// Pods renders visible fields via React after DOMContentLoaded.
	// Use a debounced MutationObserver for async renders (edit form) and
	// an immediate attempt for synchronous renders (new-item form).
	let debounceTimer = null;
	const observer = new MutationObserver( () => {
		clearTimeout( debounceTimer );
		debounceTimer = setTimeout( tryBuild, 300 );
	} );

	observer.observe( form, { childList: true, subtree: true } );

	// Try immediately in case fields are already present in the DOM.
	tryBuild();
} );
